import React from "react";
import LifeSimulator from "../../lifeSimulator";
import styled from "styled-components";
import { useElementDimensions } from "../../../hooks/use-element-size";
import RangeSlider from "../RangeSlider/RangeSlider";
import { drawGrid } from "../../utils";
import BasicButton from "../BasicButton/BasicButton";
import AdvancedControls from "../AdvancedControls/AdvancedControls";

const OuterWrapper = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  /* background-color: slateblue; */
  flex-direction: column;
  /* padding: 4px; */
`;

const CanvasWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  /* background-color: green; */
`;

const Canvas = styled.canvas`
  position: absolute;
  top: var(--top);
  left: var(--left);
  width: var(--width);
  height: var(--height);
  touch-action: none;
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  gap: 1rem;
  flex-wrap: wrap;
  width: fit-content;
  grid-area: single;
  justify-self: center;
`;

const MIN_CELL_SIZE = 8;
const MAX_CELL_SIZE = 100;
const MAX_CANVAS_WIDTH = 2000;
const MAX_CANVAS_HEIGHT = 2000;

// CR nroyalty: move to separate hook
function usePaintOnCanvas({
  canvasRef,
  lifeSim,
  cellSize,
  canvasValues,
  doDraw,
  debounceMs = 16,
}) {
  const paintState = React.useRef({
    isPainting: false,
    paintValue: null,
    lastCell: null,
  });

  const drawTimeoutRef = React.useRef(null);

  const scheduleDraw = React.useCallback(() => {
    if (drawTimeoutRef.current) return;

    drawTimeoutRef.current = setTimeout(() => {
      drawTimeoutRef.current = null;
      doDraw();
    }, debounceMs);
  }, [doDraw, debounceMs]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const { width: MAX_WIDTH, height: MAX_HEIGHT } = lifeSim.size();
    const state = paintState.current;

    function getCellFromEvent(event) {
      const { clientX, clientY } = event;
      const rect = canvas.getBoundingClientRect();
      const widthDiff = MAX_WIDTH - canvasValues.cellsWide;
      const heightDiff = MAX_HEIGHT - canvasValues.cellsHigh;
      const startingX = Math.floor(widthDiff / 2);
      const startingY = Math.floor(heightDiff / 2);

      const rawX = Math.floor((clientX - rect.left) / cellSize);
      const rawY = Math.floor((clientY - rect.top) / cellSize);

      return {
        x: startingX + rawX,
        y: startingY + rawY,
      };
    }

    function cellInBounds(cell) {
      return (
        cell.x >= 0 && cell.x < MAX_WIDTH && cell.y >= 0 && cell.y < MAX_HEIGHT
      );
    }

    function shouldSkipDraw(cell) {
      if (!cellInBounds(cell)) return true;
      if (!state.lastCell) return false;
      return cell.x === state.lastCell.x && cell.y === state.lastCell.y;
    }

    function handlePointerDown(event) {
      const cell = getCellFromEvent(event);
      if (!cellInBounds(cell)) return;

      state.isPainting = true;
      const currentValue = lifeSim.getActiveCell(cell.x, cell.y);
      state.paintValue = lifeSim.invertCell(currentValue);
      lifeSim.setActiveCell(cell.x, cell.y, state.paintValue);
      state.lastCell = cell;
      scheduleDraw();
    }

    function handlePointerMove(event) {
      if (!state.isPainting) return;
      const cell = getCellFromEvent(event);
      if (shouldSkipDraw(cell)) return;

      lifeSim.setActiveCell(cell.x, cell.y, state.paintValue);
      state.lastCell = cell;
      scheduleDraw();
    }

    function handlePointerUp(event) {
      state.isPainting = false;
      state.paintValue = null;
      state.lastCell = null;
    }

    canvas.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);

      clearTimeout(drawTimeoutRef.current);
      state.isPainting = false;
      state.paintValue = null;
      state.lastCell = null;
      drawTimeoutRef.current = null;
    };
  }, [canvasRef, cellSize, canvasValues, doDraw, lifeSim, scheduleDraw]);
}

function toFaceObject(src) {
  const path = `/faces/${src}.png`;
  const img = new Image();
  img.src = path;
  return {
    id: src,
    src: path,
    img,
  };
}

const DEFAULT_USED_FACE_SRCS = [
  "direct",
  "shades",
  "rapture",
  "smilequestion",
].map(toFaceObject);

const DEFAULT_UNUSED_FACE_SRCS = [
  "hat",
  "frown",
  "crying",
  "thinker",
  "lol",
  "lookaway",
  "blue",
].map(toFaceObject);

function saveToLocalStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function loadFromLocalStorage(key) {
  const value = localStorage.getItem(key);
  return value ? JSON.parse(value) : null;
}

const KEY_PREFIX = "morebrian-";
const KEY_CELL_SIZE = `${KEY_PREFIX}cell-size`;
const KEY_TICKS_PER_SECOND = `${KEY_PREFIX}ticks-per-second`;

function LifeGrid() {
  const wrapperRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const lifeSimulator = React.useRef(new LifeSimulator());
  const [cellSize, setCellSize] = React.useState(
    loadFromLocalStorage(KEY_CELL_SIZE) || 42
  );
  React.useEffect(() => {
    saveToLocalStorage(KEY_CELL_SIZE, cellSize);
  }, [cellSize]);

  const [playing, setPlaying] = React.useState(false);
  const [ticksPerSecond, setTicksPerSecond] = React.useState(
    loadFromLocalStorage(KEY_TICKS_PER_SECOND) || 4
  );
  React.useEffect(() => {
    saveToLocalStorage(KEY_TICKS_PER_SECOND, ticksPerSecond);
  }, [ticksPerSecond]);

  const [brianMode, setBrianMode] = React.useState(true);
  const tickSpeed = 1000 / ticksPerSecond;

  const [usedFaces, setUsedFaces] = React.useState(DEFAULT_USED_FACE_SRCS);
  const [unusedFaces, setUnusedFaces] = React.useState(
    DEFAULT_UNUSED_FACE_SRCS
  );

  const zoomDebounceRef = React.useRef(null);
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function enqueueScroll(delta) {
      if (zoomDebounceRef.current) return;
      zoomDebounceRef.current = setTimeout(() => {
        zoomDebounceRef.current = null;
        setCellSize((prev) => {
          const newSize = prev + delta;
          return Math.min(Math.max(newSize, MIN_CELL_SIZE), MAX_CELL_SIZE);
        });
      }, 20);
    }

    function handleScroll(event) {
      event.preventDefault();
      // if ctrl is held, zoom in / out by changing cell size
      if (event.ctrlKey) {
        enqueueScroll(event.deltaY < 0 ? 1 : -1);
      }
    }

    canvas.addEventListener("wheel", handleScroll);

    return () => {
      canvas.removeEventListener("wheel", handleScroll);
      clearTimeout(zoomDebounceRef.current);
      zoomDebounceRef.current = null;
    };
  });

  const { width: wrapperWidth, height: wrapperHeight } =
    useElementDimensions(wrapperRef);

  const canvasValues = React.useMemo(() => {
    let usableWidth = Math.min(wrapperWidth, MAX_CANVAS_WIDTH);
    let usableHeight = Math.min(wrapperHeight, MAX_CANVAS_HEIGHT);

    // Subtract 1 pixel for the final right / bottom border
    let maxCellsWide = Math.floor((usableWidth - 1) / cellSize);
    let maxCellsHigh = Math.floor((usableHeight - 1) / cellSize);

    // add back the 1
    const actualWidth = maxCellsWide * cellSize + 1;
    const actualHeight = maxCellsHigh * cellSize + 1;

    const widthDelta = wrapperWidth - actualWidth;
    const heightDelta = wrapperHeight - actualHeight;
    const leftPad = Math.floor(widthDelta / 2);
    const topPad = Math.floor(heightDelta / 2);

    return {
      actualWidth,
      actualHeight,
      leftPad,
      topPad,
      cellsWide: maxCellsWide,
      cellsHigh: maxCellsHigh,
    };
  }, [wrapperHeight, wrapperWidth, cellSize]);

  const doDraw = React.useCallback(() => {
    drawGrid({
      canvas: canvasRef.current,
      lifeSim: lifeSimulator.current,
      cellsWide: canvasValues.cellsWide,
      cellsHigh: canvasValues.cellsHigh,
      cellSize,
      brianMode,
      usedFaces,
    });
  }, [
    canvasValues.cellsWide,
    canvasValues.cellsHigh,
    cellSize,
    brianMode,
    usedFaces,
  ]);

  const doStep = React.useCallback(() => {
    lifeSimulator.current.step();
    doDraw();
  }, [doDraw]);

  const doClear = React.useCallback(() => {
    lifeSimulator.current.clear();
    doDraw();
  }, [doDraw]);

  // redraw when step is redefined, which should happen when the canvas size
  // changes (without this we clear the canvas on window resize)
  React.useEffect(() => {
    doDraw();
  }, [doDraw]);

  const tickInterval = React.useRef(null);
  React.useEffect(() => {
    if (!playing) {
      clearInterval(tickInterval.current);
      tickInterval.current = null;
      return;
    }

    tickInterval.current = setInterval(() => {
      doStep();
    }, tickSpeed);

    return () => {
      clearInterval(tickInterval.current);
      tickInterval.current = null;
    };
  }, [doStep, playing, tickSpeed]);

  usePaintOnCanvas({
    canvasRef,
    lifeSim: lifeSimulator.current,
    canvasValues,
    cellSize,
    doDraw,
  });

  return (
    <OuterWrapper>
      <CanvasWrapper ref={wrapperRef}>
        <Canvas
          style={{
            "--top": `${canvasValues.topPad}px`,
            "--left": `${canvasValues.leftPad}px`,
            "--width": `${canvasValues.actualWidth}px`,
            "--height": `${canvasValues.actualHeight}px`,
          }}
          width={canvasValues.actualWidth}
          height={canvasValues.actualHeight}
          ref={canvasRef}
        />
      </CanvasWrapper>
      <AdvancedControls
        brianMode={brianMode}
        setBrianMode={setBrianMode}
        lifeSim={lifeSimulator.current}
        usedFaces={usedFaces}
        setUsedFaces={setUsedFaces}
        unusedFaces={unusedFaces}
        setUnusedFaces={setUnusedFaces}
      />

      <Controls>
        <BasicButton onClick={doClear}>Clear</BasicButton>
        <BasicButton onClick={doStep}>Step</BasicButton>
        <BasicButton
          style={{ minWidth: "42px" }}
          onClick={() => setPlaying(!playing)}
        >
          {playing ? "Pause" : "Play"}
        </BasicButton>
        <RangeSlider
          title="Zoom"
          formatValue={(value) => `${value}%`}
          min={MIN_CELL_SIZE}
          max={MAX_CELL_SIZE}
          value={cellSize}
          onChange={setCellSize}
        />
        <RangeSlider
          title="Ticks / Second"
          min={1}
          max={10}
          value={ticksPerSecond}
          onChange={setTicksPerSecond}
        />
      </Controls>
    </OuterWrapper>
  );
}

export default LifeGrid;
